import { Checkbox, Link } from "@vibe/core";
import { LEGAL_POLICIES } from "../../data/constants/legalPolicies";
import type { ConsentSelections } from "../../utils/consentTypes";
import { Security } from "@vibe/icons";
import "./ConsentCheckboxes.css";

interface ConsentCheckboxesProps {
  value: ConsentSelections;
  onChange: (next: ConsentSelections) => void;
}

type CheckboxChangeEvent = { target: { checked: boolean } };

// Inline policy link that opens in a new tab and does not toggle the surrounding checkbox.
const PolicyLink = ({ href, text }: { href: string; text: string }) => (
  <Link
    href={href}
    text={text}
    target="_blank"
    inlineText
    onClick={(e: React.MouseEvent) => e.stopPropagation()}
  />
);

const REQUIRED_MARK = <span className="text-red-500"> *</span>;

const ConsentCheckboxes = ({ value, onChange }: ConsentCheckboxesProps) => {
  // Terms & privacy share a single checkbox, but stay separate in the payload so each
  // is recorded against its own policy version + hash (see LEGAL_POLICIES).
  const setLegal = (e: CheckboxChangeEvent) =>
    onChange({ ...value, terms: e.target.checked, privacy: e.target.checked });

  const setMarketing = (e: CheckboxChangeEvent) =>
    onChange({ ...value, marketing: e.target.checked });

  // Both must be granted to show as agreed. A returning user with a partial grant (e.g.
  // one policy re-versioned since they consented) sees an unchecked box and re-agrees to
  // both in one click, rather than us implying consent they never gave.
  const legalChecked = value.terms && value.privacy;

  return (
    <div className="consent-checkboxes mx-auto max-w-[520px] rounded-xl border border-solid border-gray-200 p-6 text-left">
      <div className="flex gap-2 items-center pb-4 border-0 border-b border-gray-200 border-solid">
        <Security color="#6b7280" />
        <span className="!font-figtree text-xs font-semibold tracking-wide text-gray-500">
          LEGAL AGREEMENTS &amp; CONSENT
        </span>
      </div>

      <p className="!font-figtree !mt-4 text-sm text-gray-500">
        Please review and agree below. Your consent is recorded and you can withdraw anytime.
      </p>

      <div className="flex flex-col gap-4 mt-4">
        <Checkbox
          checked={legalChecked}
          onChange={setLegal}
          label={
            <span className="!font-figtree text-sm">
              I agree to the{" "}
              <PolicyLink href={LEGAL_POLICIES.terms.url} text="Terms & Conditions" /> and{" "}
              <PolicyLink href={LEGAL_POLICIES.privacy.url} text="Privacy Policy" />, including data
              processing and GDPR data handling practices.{REQUIRED_MARK}
            </span>
          }
        />
        <Checkbox
          checked={value.marketing}
          onChange={setMarketing}
          label={
            <span className="!font-figtree text-sm">
              I&apos;d like to receive newsletters and offers from Pioneera.
            </span>
          }
        />
      </div>

      <hr className="my-4 border-0 border-t border-gray-200 border-solid" />

      <p className="!font-figtree text-xs text-gray-500">
        By continuing, you confirm you&apos;re at least 18 years old (or your country&apos;s digital
        consent age).
      </p>
    </div>
  );
};

export default ConsentCheckboxes;
export { ConsentCheckboxes };
